import {
  getRecentNetworkSamples,
  getRecentRequestSamples,
  getRecentThroughputSamples,
  LATENCY_SOURCE,
  LatencySource,
} from '../../../services/networkLatencyObserver';
import { LATENCY_WINDOW_SIZE } from '../NetworkStatusProvider.constants';
import { NetworkSignals } from '../NetworkStatusProvider.types';

import { computeWeightedAverageLatency } from './computeWeightedAverageLatency';
import { getIsOnline, getNetworkConnection } from './getNetworkConnection';

const computeMeasuredDownlinkMbps = (
  throughputMbpsValues: number[],
): number | undefined => {
  if (throughputMbpsValues.length === 0) {
    return undefined;
  }

  return Math.max(...throughputMbpsValues);
};

/**
 * Strictest-first: a single-RTT source (probe/server-timing) carries a tighter
 * acceptable bound than a multi-RTT handshake, so it must win when both are in
 * the window — otherwise a sporadic `connection` sample would relax the bound.
 */
const NETWORK_SOURCE_PRECEDENCE = [
  LATENCY_SOURCE.PROBE,
  LATENCY_SOURCE.SERVER_TIMING,
  LATENCY_SOURCE.CONNECTION,
] as const;

const resolveDominantLatencySource = (
  sources: Array<LatencySource | undefined>,
): LatencySource | undefined => {
  const presentSources = new Set(sources);

  return NETWORK_SOURCE_PRECEDENCE.find((source) =>
    presentSources.has(source),
  );
};

export const readNetworkSignals = (): NetworkSignals => {
  const connection = getNetworkConnection();
  const requestSamples = getRecentRequestSamples(LATENCY_WINDOW_SIZE);
  const networkSamples = getRecentNetworkSamples(LATENCY_WINDOW_SIZE);
  const throughputSamples = getRecentThroughputSamples(LATENCY_WINDOW_SIZE);

  const measuredNetworkLatencyMs = computeWeightedAverageLatency(
    networkSamples,
    (sample) => sample.networkLatencyMs,
  );

  const hasNetworkMeasurement = measuredNetworkLatencyMs !== undefined;

  const rollingAverageLatencyMs = hasNetworkMeasurement
    ? measuredNetworkLatencyMs
    : computeWeightedAverageLatency(requestSamples);

  const latencySource = hasNetworkMeasurement
    ? resolveDominantLatencySource(
        networkSamples.map((sample) => sample.latencySource),
      )
    : LATENCY_SOURCE.REQUEST;

  const measuredDownlinkMbps = computeMeasuredDownlinkMbps(
    throughputSamples
      .map((sample) => sample.throughputMbps)
      .filter((throughputMbps) => throughputMbps !== undefined),
  );

  const timeoutCountInWindow = requestSamples.filter(
    (sample) => sample.isTimeout,
  ).length;

  return {
    isOnline: getIsOnline(),
    effectiveType: connection?.effectiveType,
    rtt: connection?.rtt,
    downlink: connection?.downlink,
    measuredDownlinkMbps,
    rollingAverageLatencyMs,
    hasNetworkMeasurement,
    latencySource,
    timeoutCountInWindow,
    sampleCount: requestSamples.length + networkSamples.length,
  };
};
